<template>
  <div id="app" class="tex2jax_ignore">
    <!-- Sidebar menu -->
    <div class="ui vertical inverted sidebar menu">
      <a class="item"  @click="hideSidebar()">&nbsp;<i class="close icon"></i></a>
      <a class="item" :class="{active: $route.path === '/'}" v-show="ready" @click="sidebarGo('/')">{{ text.navHome }}</a>
      <a class="item" :class="{active: $route.path.substr(0, 10) === '/memo-sets'}" v-show="ready && user.id" @click="sidebarGo('/memo-sets')">{{ text.navMemoSets }}</a>
      <a class="item" :class="{active: $route.path.substr(0, 17) === '/shared-memo-sets'}" v-show="ready && user.id" @click="sidebarGo('/shared-memo-sets')">{{ text.navSharedMemoSets }}</a>
      <a class="item" :class="{active: $route.path.substr(0, 17) === '/public-memo-sets'}" v-show="ready && user.id" @click="clearSearch(); sidebarGo('/public-memo-sets')">{{ text.navPublicMemoSets }}</a>
      <a class="item" href="https://forum.artofmemory.com" target="_blank" rel="noopener" v-show="ready">{{ text.navForum }}<i class="external icon"></i></a>
      <a class="item" href="https://memoryleague.com" target="_blank" rel="noopener" v-show="ready">Memory League<i class="external icon"></i></a>
      <a class="item" href="https://artofmemory.com/app" target="_blank" rel="noopener" v-show="ready">{{ text.navTutorials }}<i class="external icon"></i></a>
      <a class="item" href="https://artofmemory.com/wiki" target="_blank" rel="noopener" v-show="ready">Wiki<i class="external icon"></i></a>
    </div>
    <div class="pusher">
      <!-- Main navbar -->
      <div class="ui fixed inverted menu noPrint">
        <div class="ui fluid container">
          <router-link class="header item memQ" :class="{'mobile': mobile}" to="/" active-class="none" exact><div class="memQ1">Mem</div><div class="memQ2">Q</div></router-link>
          <a class="item" v-show="mobile" @click="showSidebar"><i class="sidebar icon"></i></a>
          <router-link v-show="ready && !mobile" class="item main" to="/" exact>{{ text.navHome }}</router-link>
          <router-link v-show="ready && !mobile && user.id" class="item main" to="/memo-sets">{{ text.navMemoSets }}</router-link>
          <router-link v-show="ready && !mobile && user.id" class="item main" to="/shared-memo-sets">{{ text.navSharedMemoSets }}</router-link>
          <router-link v-show="ready && !mobile" class="item main" to="/public-memo-sets" @click.native="clearSearch()">{{ text.navPublicMemoSets }}</router-link>
          <div v-show="ready && !mobile" class="ui simple dropdown item">
            <i class="ellipsis vertical icon"></i>
            <div class="menu">
              <a v-show="ready && !mobile" class="item" href="https://forum.artofmemory.com" target="_blank" rel="noopener">
                <span class="moreOptionsItem">{{ text.navForum }}</span>
                <i class="external icon"></i>
              </a>
              <a v-show="ready && !mobile" class="item" href="https://app.memoryleague.com" target="_blank" rel="noopener">
                <span class="moreOptionsItem">Memory League</span>
                <i class="external icon"></i></a>
              <a v-show="ready && !mobile" class="item" href="https://artofmemory.com/app" target="_blank" rel="noopener">
                <span class="moreOptionsItem">{{ text.navTutorials }}</span>
                <i class="external icon"></i>
              </a>
              <a v-show="ready && !mobile" class="item" href="https://artofmemory.com/wiki" target="_blank" rel="noopener">
                <span class="moreOptionsItem">Wiki</span>
                <i class="external icon"></i>
              </a>
            </div>
          </div>
          <div class="right menu" v-show="ready">
            <!-- Info menu -->
            <div class="ui dropdown item narrow">
              <i class="info circle icon"></i>
              <div class="ui menu">
                <div class="header">{{ text.navInfoTitle }}</div>
                <div class="divider"></div>
                <router-link class="item notActive" :to="'/guide'" target="_blank" rel="noopener">{{ text.navInfoGuide }}</router-link>
                <router-link class="item" to="/contact-us">{{ text.navInfoContactUs }}</router-link>
                <a class="item notActive" href="https://artofmemory.com/legal/terms" target="_blank" rel="noopener">{{ text.navInfoTerms }}</a>
                <a class="item notActive" href="https://artofmemory.com/legal/privacy" target="_blank" rel="noopener">{{ text.navInfoPrivacy }}</a>
              </div>
            </div>
            <!-- Language menu -->
            <div class="ui dropdown item narrow">
              <i :class="user.uiLanguage || 'us'" class="flag noRightMargin"></i>
              <div class="ui menu">
                <div class="header">{{ text.navLanguageTitle }}</div>
                <div class="divider"></div>
                <div class="item" v-for="language in languages" :key="language.countryCode" @click="setLanguage(language.countryCode)">
                  <i :class="language.countryCode" class="flag"></i> {{ language.name }}
                </div>
              </div>
            </div>
            <!-- User menu -->
            <div class="ui dropdown item narrow" :class="{'desktopMenuRightMargin': !mobile}" v-show="user.id">
              <i class="user icon"></i>
              <div class="ui menu userMenu">
                <div class="header">{{ user.displayName }}</div>
                <div class="divider"></div>
                <a class="item notActive" href="https://artofmemory.com/user" target="_blank" rel="noopener">{{ text.navUserMyAccount }}</a>
                <div class="item" @click="signOutClick()">{{ text.navUserSignOut }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="ui fluid wrapper" @touchstart="touch = true">
        <signin v-if="!user.id"></signin>
        <router-view
          v-show="user.id"
          :key="$route.fullPath"
          :memo-sets="memoSets"
          :mobile="mobile"
          :shared-memo-sets="sharedMemoSets"
          :statuses="statuses"
          :text="text"
          :touch="touch"
          :user="user"
          :window="window"
        >
        </router-view>
      </div>
    </div>
  </div>
</template>

<script>
import Signin from '@/components/Signin'

const $ = window.$
const firebase = window.firebase
const db = firebase.firestore()

function logError (errorCode, error) {
  console.log(errorCode)
  console.log(error)
}

export default {
  name: 'app',
  components: {
    Signin
  },
  data () {
    return {
      appPathPrefix: window.location.hostname === 'localhost' ? '' : '',
      detachKnownDataListener: null,
      detachMemoSetsListener: null,
      detachSharedMemoSetsListener: null,
      detachTextListener: null,
      detachUsersListener: null,
      memoSets: null,
      knownData: null,
      languages: [
        { countryCode: 'cn', name: '中文' },
        { countryCode: 'de', name: 'Deutsch' },
        { countryCode: 'gb', name: 'English (UK)' },
        { countryCode: 'us', name: 'English (US)' },
        { countryCode: 'es', name: 'Español' },
        { countryCode: 'fr', name: 'Français' }
      ],
      ready: false,
      sharedMemoSets: null,
      statuses: {},
      statusesTimeout: null,
      text: {},
      textEnglish: {
        commonAdd: 'Add',
        commonCancel: 'Cancel',
        commonDelete: 'Delete',
        commonOK: 'OK',
        commonRedo: 'Redo',
        commonRows: 'Rows',
        commonSearchPlaceholder: 'Search...',
        commonStatus: 'Status',
        commonUndo: 'Undo',
        memoSetActions: 'Actions',
        memoSetAddAudio: 'Add Audio',
        memoSetAddColumn: 'Add Column',
        memoSetAddImage: 'Add Image',
        memoSetAddQuestion: 'Add Question',
        memoSetAddRows: 'Add Rows',
        memoSetAddToMyMemoSets: 'Add to My Memo Sets',
        memoSetAll: 'All',
        memoSetAlreadyPublic: 'This memo set is already shared publicly.',
        memoSetApplyToSubsequentRowsLeft: 'Apply to',
        memoSetApplyToSubsequentRowsRight: 'subsequent rows',
        memoSetAsAbove: 'as above',
        memoSetAuthor: 'Author',
        memoSetBackgroundPhoto: 'Background Photo',
        memoSetClip: 'Clip',
        memoSetColumns: 'Columns',
        memoSetCongrats: 'Congratulations, you’re up to date for this memo set!',
        memoSetCongratsContinue: 'You can continue reviewing if you like.',
        memoSetCongratsGroup: 'Congratulations, you’re up to date for the selected group!',
        memoSetCongratsGroups: 'Congratulations, you’re up to date for the selected groups!',
        memoSetCongratsQuestion: 'Congratulations, you’re up to date for the selected question!',
        memoSetCongratsQuestions: 'Congratulations, you’re up to date for the selected questions!',
        memoSetCongratsQuestionGroup: 'Congratulations, you’re up to date for the selected question and group!',
        memoSetCongratsQuestionGroups: 'Congratulations, you’re up to date for the selected question and groups!',
        memoSetCongratsQuestionsGroup: 'Congratulations, you’re up to date for the selected questions and group!',
        memoSetCongratsQuestionsGroups: 'Congratulations, you’re up to date for the selected questions and groups!',
        memoSetCopy: 'Copy',
        memoSetCopyMemoSet: 'Copy Memo Set',
        memoSetCoverImage: 'Cover Image',
        memoSetDefaultColumn1: 'Column 1',
        memoSetDefaultColumn2: 'Column 2',
        memoSetDefaultDescription: 'Enter any notes about the memo set here...',
        memoSetDefaultQuestion: 'What is the <toField> for <fromField> *?',
        memoSetDefaultQuestionFromImage: 'What is the <toField> for this <fromField>?<br><br>*',
        memoSetDefaultQuestionFromImageToSummaryImage: 'What is the summary image for this <fromField>?<br><br>*',
        memoSetDefaultQuestionFromSummaryImage: 'What is the <toField> for this summary image?<br><br>*',
        memoSetDefaultQuestionToSummaryImage: 'What is the summary image for <fromField> *?',
        memoSetDelete: 'Delete',
        memoSetDeleteColumn: 'Delete Column',
        memoSetDeleteImage: 'Delete Image',
        memoSetDeleteMemoSet: 'Delete Memo Set',
        memoSetDeleteMemoSetContent: 'Are you sure you want to delete the memo set *?',
        memoSetDeleteRows: 'Delete Rows',
        memoSetDescription: 'Description',
        memoSetEditQuestion: 'Edit Question',
        memoSetErase: 'Erase',
        memoSetExample: 'Example',
        memoSetExportData: 'Export Data',
        memoSetExportSuccessful: 'Export Successful',
        memoSetExportSuccessfulDetail: 'The memo set’s data has been copied to your clipboard, and can now be pasted into a spreadsheet or text document.',
        memoSetExportUnsuccessful: 'Export Unsuccessful',
        memoSetExportUnsuccessfulDetail: 'Oops, something has gone wrong. The memo set’s data was not exported.',
        memoSetFlipHorizontal: 'Flip Horizontal',
        memoSetFlipVertical: 'Flip Vertical',
        memoSetFolder: 'Folder',
        memoSetFromColumn: 'From Column',
        memoSetGoToMyMemoSet: 'Go to My Memo Set',
        memoSetGroup: 'Group',
        memoSetGroups: 'Groups',
        memoSetLearn: 'Learn',
        memoSetMoveColumn: 'Move Column',
        memoSetMoveRows: 'Move Rows',
        memoSetMyRating: 'My Rating',
        memoSetNewFolderLabel: 'New Folder',
        memoSetNewFolderItem: '(New Folder)',
        memoSetNewNamePre: 'Copy of ',
        memoSetNewNamePost: '',
        memoSetNextRows: 'Next Rows',
        memoSetNoData: 'No data!',
        memoSetNoGroups: 'No groups are selected!',
        memoSetNone: 'None',
        memoSetNoQuestionsDefinedHdr: 'No questions are defined!',
        memoSetNoQuestionsDefined: 'Add a question to use the Learn feature.',
        memoSetNoQuestionsSelected: 'No questions are selected!',
        memoSetNotes: 'Notes',
        memoSetOptions: 'Options',
        memoSetOriginalAuthor: 'Original Author',
        memoSetPanLock: 'Pan Lock',
        memoSetPasteImageHere: 'Paste image here...',
        memoSetPopulateColumn: 'Populate Column',
        memoSetPreviousRows: 'Previous Rows',
        memoSetPrint: 'Print',
        memoSetPublic: 'Public',
        memoSetQuestion: 'Question',
        memoSetQuestions: 'Questions',
        memoSetRemoveColor: 'Remove Color',
        memoSetResetLearning: 'Reset Learning',
        memoSetReset: 'Are you sure you want to reset your learning data for this memo set?',
        memoSetResetGroup: 'Are you sure you want to reset your learning data for the selected group?',
        memoSetResetGroups: 'Are you sure you want to reset your learning data for the selected groups?',
        memoSetResetQuestion: 'Are you sure you want to reset your learning data for the selected question?',
        memoSetResetQuestions: 'Are you sure you want to reset your learning data for the selected questions?',
        memoSetResetQuestionGroup: 'Are you sure you want to reset your learning data for the selected question and group?',
        memoSetResetQuestionGroups: 'Are you sure you want to reset your learning data for the selected question and groups?',
        memoSetResetQuestionsGroup: 'Are you sure you want to reset your learning data for the selected questions and group?',
        memoSetResetQuestionsGroups: 'Are you sure you want to reset your learning data for the selected questions and groups?',
        memoSetRow: 'Row',
        memoSetSendToBack: 'Send to Back',
        memoSetSetCoverImage: 'Set Cover Image',
        memoSetShare: 'Share',
        memoSetSharedWith: 'Shared With',
        memoSetSortRows: 'Sort Rows',
        memoSetSuitable: 'Please ensure that any content shared publicly is suitable for children.',
        memoSetSummaryImage: 'Summary Image',
        memoSetTabData: 'Data',
        memoSetTabInfo: 'Info',
        memoSetTabLearn: 'Learn',
        memoSetTabQuestions: 'Questions',
        memoSetTitle: 'Title',
        memoSetToColumn: 'To Column',
        memoSetUploadingImage: 'Uploading image...',
        memoSetUploadingImages: 'Uploading <remaining> images...',
        memoSetUseBackgroundPhotos: 'Use Background Photos',
        memoSetUseGroups: 'Use Groups',
        memoSetUsernameOrEmail: 'Username or email address...',
        memoSetUseSummaryImages: 'Use Summary Images',
        memoSetViewQuestion: 'Question',
        memoSetWalkThrough: 'Walk Through',
        memoSetZoomIn: 'Zoom In',
        memoSetZoomOut: 'Zoom Out',
        memoSetZoomToSelected: 'Zoom to Selected',
        memoSetsAddAFolder: 'Add a folder',
        memoSetsAddAMemoSet: 'Add a memo set',
        memoSetsColumnImage: 'Image',
        memoSetsColumnMemoSet: 'Memo Set',
        memoSetsColumnRows: 'Rows',
        memoSetsDefaultTitle: 'New memo set',
        memoSetsNewFolder: 'New Folder',
        navForum: 'Forum',
        navHome: 'Home',
        navInfoContactUs: 'Contact Us',
        navInfoGuide: 'Guide',
        navInfoPrivacy: 'Privacy Policy',
        navInfoTerms: 'Terms of Use',
        navInfoTitle: 'Information',
        navLanguageTitle: 'Language',
        navMemoSets: 'My Memo Sets',
        navPublicMemoSets: 'Public Memo Sets',
        navSharedMemoSets: 'Shared With Me',
        navTutorials: 'Tutorials',
        navUserMyAccount: 'My Account',
        navUserSignOut: 'Sign Out',
        publicAuthor: 'Author',
        publicNoMemoSets: 'No memo sets were found.',
        publicRating: 'Average Rating'
      },
      touch: false,
      user: {},
      window: {
        height: window.innerHeight,
        width: window.innerWidth
      }
    }
  },
  computed: {
    mobile: function () {
      return this.window.width < 767
    }
  },
  created () {
    window.vmApp = this
    const self = this
    // Add window resize listener
    window.addEventListener('resize', function (event) {
      self.window.height = window.innerHeight
      self.window.width = window.innerWidth
    })
    firebase.auth().onAuthStateChanged(function (user) {
      if (self.detachUsersListener) {
        self.detachUsersListener()
        self.detachUsersListener = null
      }
      if (self.detachKnownDataListener) {
        self.detachKnownDataListener()
        self.detachKnownDataListener = null
      }
      if (self.detachMemoSetsListener) {
        self.detachMemoSetsListener()
        self.detachMemoSetsListener = null
      }
      if (self.detachSharedMemoSetsListener) {
        self.detachSharedMemoSetsListener()
        self.detachSharedMemoSetsListener = null
      }
      if (user) {
        // Listen to user data
        self.detachUsersListener = db.doc('users/' + user.uid)
          .onSnapshot(doc => {
            if (doc.exists) {
              // Save previous language code
              let previousUiLanguage = self.user.uiLanguage || ''
              // Update user data
              self.user = doc.data()
              self.user.id = user.uid
              // Load text if we don't already have it
              if (self.user.uiLanguage !== previousUiLanguage) {
                self.loadText()
              }
            }
          }, error => { logError('App_onAuthStateChanged_1', error) }
          )
        // Listen to memo sets data
        self.detachMemoSetsListener = db.collection('users/' + user.uid + '/memoSets')
          .onSnapshot(snapshot => {
            // Populate memoSets array
            self.memoSets = []
            snapshot.forEach(doc => {
              let memoSetIds = Object.keys(doc.data())
              memoSetIds.forEach(memoSetId => {
                let memoSet = doc.data()[memoSetId]
                memoSet.docId = doc.id
                memoSet.memoSetId = memoSetId
                self.memoSets.push(memoSet)
              })
            })
          }, error => { logError('App_onAuthStateChanged_2', error) }
          )
        // Listen to known data
        self.detachKnownDataListener = db.doc('users/' + user.uid + '/knownData/doc')
          .onSnapshot(doc => {
            self.knownData = doc.data() || {}
            self.calculateStatuses()
          }, error => { logError('App_onAuthStateChanged_3', error) }
          )
        // Listen to shared with me data
        self.detachSharedMemoSetsListener = db.collection('users/' + user.uid + '/sharedWithMe')
          .onSnapshot(snapshot => {
            self.sharedMemoSets = []
            snapshot.forEach(doc => {
              let memoSet = doc.data()
              memoSet.memoSetId = doc.id
              self.sharedMemoSets.push(memoSet)
            })
          }, error => { logError('App_onAuthStateChanged_4', error) }
          )
      } else {
        self.user = {
          uiLanguage: 'us'
        }
        self.loadText()
        // START PROTOTYPE CODE
        /*
        const xhr = new XMLHttpRequest()
        xhr.open('POST', '/server', true)
        xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded')
        xhr.onreadystatechange = () => {
          if (this.readyState === XMLHttpRequest.DONE && this.status === 200) {
            // TODO - write this
          }
        }
        xhr.send()
        */
        // END PROTOTYPE CODE
      }
    })
  },
  mounted () {
    // Initialize dropdown in menu
    $('.ui.dropdown').dropdown()
  },
  methods: {
    calculateStatuses: function () {
      let adjustedBaseTimestamp, adjustedNextDue, adjustedNow, adjustedTimestamp, dueCount, extraDueCount, knownCount, memoSetKnownData, unknownCount
      // Cancel any pending timeout
      if (this.statusesTimeout !== null) {
        clearTimeout(this.statusesTimeout)
        this.statusesTimeout = null
      }
      this.statuses = {}
      adjustedNow = Math.round(Date.now() / 10000)
      adjustedNextDue = Infinity
      // For each memo set
      Object.keys(this.knownData).forEach(memoSetId => {
        // Determine statuses from known data
        memoSetKnownData = this.knownData[memoSetId]
        adjustedBaseTimestamp = memoSetKnownData[2]
        unknownCount = memoSetKnownData[0]
        extraDueCount = memoSetKnownData.filter((adjustedOffset, index) => index > 2 && adjustedBaseTimestamp + adjustedOffset <= adjustedNow).length
        dueCount = memoSetKnownData[1] + extraDueCount
        knownCount = memoSetKnownData.length - 3 - extraDueCount
        this.statuses[memoSetId] = {
          due: dueCount,
          known: knownCount,
          unknown: unknownCount
        }
        // Check whether the next due timestamp for the memo set is the least so far
        for (let i = 3; i < memoSetKnownData.length; i++) {
          adjustedTimestamp = adjustedBaseTimestamp + memoSetKnownData[i]
          if (adjustedTimestamp <= adjustedNow) continue
          // Here, memoSetKnownData[i] is the closest future timestamp
          if (adjustedTimestamp < adjustedNextDue) {
            adjustedNextDue = adjustedTimestamp
          }
          break
        }
      })
      // Schedule the next calculation
      if (adjustedNextDue !== Infinity) {
        this.statusesTimeout = setTimeout(this.calculateStatuses, (adjustedNextDue - adjustedNow) * 10000)
      }
    },
    clearSearch: function () {
      if (window.aoms && window.aoms.memoSetSearchText) {
        window.aoms.memoSetSearchText = ''
      }
    },
    hideSidebar: function () {
      $('.ui.sidebar').sidebar('hide')
    },
    loadText: function () {
      const self = this
      // Detach previous text listener, if any
      if (this.detachTextListener) {
        this.detachTextListener()
        this.detachTextListener = null
      }
      if (this.user.uiLanguage === 'us') {
        this.text = Object.assign({}, this.textEnglish)
        this.ready = true
      } else {
        // Listen to text in user's language
        this.detachTextListener = db.doc('text/' + this.user.uiLanguage)
          .onSnapshot(doc => {
            if (doc.exists) {
              // Start with English text
              self.text = Object.assign({}, self.textEnglish)
              // Apply user's language text
              self.text = Object.assign({}, self.text, doc.data())
              self.ready = true
            }
          }, error => { logError('App_loadText', error) }
          )
      }
    },
    setLanguage: function (countryCode) {
      // Update users record
      db.doc('users/' + this.user.id)
        .update({
          uiLanguage: countryCode
        })
        .catch(error => { logError('App_setLanguage', error) })
    },
    showSidebar: function () {
      $('.ui.sidebar')
        .sidebar({
          context: '#app',
          dimPage: false,
          onHide: () => {
            // Revert history if not already done - this covers the case when the user closes the modal without using the back button
            if (window.history.state.sidebar) {
              window.history.back()
            }
          },
          onShow: () => {
            // Push to history so that back button closes the sidebar
            window.history.pushState({ sidebar: true }, '', window.location.href)
            // Hide sidebar on history entry change
            window.onpopstate = () => {
              $('.ui.sidebar').sidebar('hide')
            }
          }
        })
        .sidebar('setting', 'transition', 'overlay')
        .sidebar('setting', 'mobileTransition', 'overlay')
        .sidebar('show')
    },
    sidebarGo: function (to) {
      // Note: route to new page before hiding the sidebar so that window.history.state.sidebar isn't true when the sidebar is hidden
      // Route to selected page
      this.$router.push(to)
      // Close sidebar
      $('.ui.sidebar').sidebar('hide')
    },
    signOutClick: function () {
      const self = this
      if (this.detachUsersListener) {
        this.detachUsersListener()
        this.detachUsersListener = null
      }
      firebase.auth().signOut().then(function () {
        self.$router.push('/')
      })
    }
  }
}
</script>

<style>
  body {
    background-color: #f9f9f9;
  }
  .innerWrapper {
    /* Giving the innerWrapper a small fixed height prevents an outer scrollbar */
    height: 100px;
    margin: 1rem 1.5rem;
  }
  .innerWrapper.mobile {
    margin: 1rem 0 0 0;
  }
 </style>

<style scoped>
  #app {
    font-family: 'Avenir', Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    color: #2c3e50;
  }
  #app, .pusher {
    height: 100%;
  }
  /* Wrapper around content */
  .wrapper {
    padding-top: 2.85rem;
  }
  /* Override Semantic UI style to prevent content becoming unscrollable after sidebar has been opened */
  .pushable > .pusher {
    overflow: auto;
  }
  /* Override Semantic UI style for iOS */
  html.ios, html.ios body {
    height: 100% !important;
  }
  /* Remove Semantic UI's space on right after icons */
  .ui.menu .item>i.icon {
    margin-right: 0;
  }
  /* Reduce padding on icon dropdowns */
  .ui.dropdown.item.narrow {
    padding-left: 0.8rem;
    padding-right: 0.8rem;
  }
  .noRightMargin {
    margin-right: 0 !important;
  }
  /* Override menu header style */
  .ui.dropdown .menu>.header {
    color: #8c8c8c;
    font-size: 110%;
    text-transform: none;
  }
  /* Position user menu to keep contents in the view area */
  .userMenu {
    left: auto !important;
    right: -2.7rem !important;
  }
  /* Avoid leaving menu items active after click */
  .ui.menu .ui.dropdown .menu>.active.item.notActive {
    background-color: transparent !important;
    font-weight: normal !important;
  }
  /* Still use hover style */
  .ui.menu .ui.dropdown .menu>.active.item.notActive:hover {
    background-color: rgba(0,0,0,.05) !important;
  }
  /* Active route style */
  .router-link-active {
    background-color: rgba(255,255,255,.15) !important;
  }
  /* Header bar */
  .ui.inverted.menu {
    background-color: #222222;
  }
  /* Add space before loader */
  .loader {
    margin: 1rem !important;
  }
  .desktopMenuRightMargin {
    margin-right: 1rem;
  }
  .moreOptionsItem {
    display: inline-block;
    width: 10rem;
  }
  /* MemQ styles */
  .memQ {
    font-family: 'ZCOOL XiaoWei';
    font-size: 120%;
    padding: 0 2rem 0 1.5rem !important;
  }
  .memQ.mobile {
    padding: 0 1.5rem 0 1rem !important;
  }
  .memQ1 {
    color: gold;
    margin-top: -1em;
  }
  .memQ2 {
    color: darkorange;
    margin-left: -1.14em;
    margin-top: 0.7em;
  }
  .item.main {
    min-width: 7rem;
    text-align: center;
  }
  /* display:block to allow text to be centered */
  .ui.menu:not(.vertical) .item.main {
    display: block;
  }
</style>



// WEBPACK FOOTER //
// src/App.vue